/** E3 (spec 2026-09-18 §6): Customize draft state for a platform
 * `kind: process` row. Same posture as workflowConfigStore: the overlay is
 * SPARSE — every setter compares against the base and prunes a value that
 * equals it, so "set it back" is the same document as "never touched it".
 * Addresses are definition-shaped (spec D14): `steps.<id>.executor.seat`. */
import { create } from 'zustand'
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { WorkflowConfigValidateResponse, WorkflowOverlayJson } from '@/types/workflowConfig'

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/** Key-order-insensitive: a prune-and-restore re-inserts a section at the
 * END of the overlay, so the same content can arrive in another key order. */
function sameContent(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
  if (Array.isArray(a) || Array.isArray(b)) return same(a, b)
  const ak = Object.keys(a)
  const bk = Object.keys(b)
  return (
    ak.length === bk.length &&
    ak.every((k) => k in b && sameContent((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
  )
}

function readPath(obj: unknown, path: string[]): unknown {
  let node: unknown = obj
  for (const seg of path) {
    if (!node || typeof node !== 'object' || !(seg in (node as Record<string, unknown>))) return undefined
    node = (node as Record<string, unknown>)[seg]
  }
  return node
}

/** Deletes every empty object along `path`, deepest first. */
function pruneEmpty(obj: Record<string, unknown>, path: string[]): void {
  if (path.length === 0) return
  const [head, ...rest] = path
  const child = obj[head]
  if (rest.length > 0 && child && typeof child === 'object') pruneEmpty(child as Record<string, unknown>, rest)
  const v = obj[head]
  if (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) delete obj[head]
}

function setOrPrune(
  overlay: WorkflowOverlayJson,
  path: string[],
  value: unknown,
  baseValue: unknown,
): WorkflowOverlayJson {
  const next = clone(overlay) as Record<string, unknown>
  let cursor = next
  for (const seg of path.slice(0, -1)) {
    cursor[seg] = { ...(cursor[seg] as Record<string, unknown> | undefined) }
    cursor = cursor[seg] as Record<string, unknown>
  }
  const leaf = path[path.length - 1]
  // Clearing a text the base never had is "untouched", not an empty override.
  const untouched = same(value, baseValue) || (value === '' && baseValue == null)
  if (untouched) delete cursor[leaf]
  else cursor[leaf] = value
  pruneEmpty(next, path.slice(0, -1))
  return next as WorkflowOverlayJson
}

/** The base's EFFECTIVE value at a step path. A person/ai executor's `form`
 * of null means the step's `produces` (backend `ProcessStep.form_fields`),
 * so re-ticking every box is the base, not an override. */
function baseStepValue(base: ProcessWorkflowDefinitionJson | null, stepId: string, path: string[]): unknown {
  const step = base?.steps?.[stepId]
  const raw = readPath(step, path)
  if (raw == null && path.join('.') === 'executor.form') return step?.produces ?? []
  // A due ADDED through the overlay defaults its calendar to `clock` (spec §3.2).
  if (raw == null && path.join('.') === 'due.calendar') return 'clock'
  return raw
}

interface ProcessConfigState {
  workflowId: string | null
  base: ProcessWorkflowDefinitionJson | null
  overlay: WorkflowOverlayJson
  savedDraft: WorkflowOverlayJson | null
  /** SA-416: what this session's writes are based on — the stored draft's
   * stamp (null = no stamped draft) and the config base's version number. */
  expectedUpdatedAt: string | null
  baseVersionNumber: number | null
  /** SA-1410: the live version number the session began on (null = none). */
  versionNumber: number | null
  editing: boolean
  validation: WorkflowConfigValidateResponse | null
  beginEditing(
    workflowId: string,
    base: ProcessWorkflowDefinitionJson,
    savedDraft: WorkflowOverlayJson | null,
    expectedUpdatedAt?: string | null,
    baseVersionNumber?: number | null,
    versionNumber?: number | null,
  ): void
  stopEditing(): void
  /** Records a save's answer. `workflowId` is the session the save STARTED in:
   * an answer that lands after Exit or after navigating to another workflow
   * (SA-1410) changes nothing — it must not plant its stamp on the next session. */
  markSaved(draft: WorkflowOverlayJson, updatedAt: string | null | undefined, workflowId: string): void
  /** SA-1410: the session moves onto what is stored and live now — `start`
   * becomes the overlay its edits are measured against, `overlay` (the
   * caller's rebase of the edits over `start`) the working copy, with the
   * new base, stamp and numbers. Validation is for the old basis; drop it. */
  rebaseOnto(
    base: ProcessWorkflowDefinitionJson,
    start: WorkflowOverlayJson,
    overlay: WorkflowOverlayJson,
    expectedUpdatedAt: string | null,
    baseVersionNumber: number | null,
    versionNumber: number | null,
  ): void
  /** `path` is relative to the step, e.g. `['executor', 'seat']`. */
  setStepParam(stepId: string, path: string[], value: unknown): void
  setSeatRoles(seatId: string, roles: string[]): void
  setSeatLabel(seatId: string, lang: string, text: string): void
  setFieldText(fieldId: string, key: 'label' | 'help', lang: string, text: string): void
  setLanguages(languages: string[]): void
  setValidation(v: WorkflowConfigValidateResponse | null): void
  isDirty(): boolean
  overlaySnapshot(): WorkflowOverlayJson
}

export const useProcessConfigStore = create<ProcessConfigState>((set, get) => {
  // A validation result is for ONE overlay; any edit makes it stale.
  const edit = (next: (s: ProcessConfigState) => WorkflowOverlayJson) =>
    set((s) => ({ overlay: next(s), validation: null }))
  return {
    workflowId: null,
    base: null,
    overlay: {},
    savedDraft: null,
    expectedUpdatedAt: null,
    baseVersionNumber: null,
    versionNumber: null,
    editing: false,
    validation: null,
    beginEditing: (workflowId, base, savedDraft, expectedUpdatedAt = null, baseVersionNumber = null, versionNumber = null) =>
      set({
        workflowId,
        base,
        overlay: savedDraft ? clone(savedDraft) : {},
        savedDraft: savedDraft ? clone(savedDraft) : null,
        expectedUpdatedAt,
        baseVersionNumber,
        versionNumber,
        editing: true,
        validation: null,
      }),
    stopEditing: () =>
      set({
        workflowId: null,
        base: null,
        overlay: {},
        savedDraft: null,
        expectedUpdatedAt: null,
        baseVersionNumber: null,
        versionNumber: null,
        editing: false,
        validation: null,
      }),
    markSaved: (draft, updatedAt, workflowId) => {
      if (get().workflowId !== workflowId) return
      set(updatedAt === undefined ? { savedDraft: clone(draft) } : { savedDraft: clone(draft), expectedUpdatedAt: updatedAt })
    },
    rebaseOnto: (base, start, overlay, expectedUpdatedAt, baseVersionNumber, versionNumber) =>
      set({
        base,
        savedDraft: clone(start),
        overlay: clone(overlay),
        expectedUpdatedAt,
        baseVersionNumber,
        versionNumber,
        validation: null,
      }),
    setStepParam: (stepId, path, value) =>
      edit((s) => setOrPrune(s.overlay, ['steps', stepId, ...path], value, baseStepValue(s.base, stepId, path))),
    setSeatRoles: (seatId, roles) =>
      edit((s) => setOrPrune(s.overlay, ['seats', seatId, 'roles'], roles, s.base?.seats?.[seatId]?.roles ?? [])),
    setSeatLabel: (seatId, lang, text) =>
      edit((s) => setOrPrune(s.overlay, ['seats', seatId, 'label', lang], text, s.base?.seats?.[seatId]?.label?.[lang])),
    setFieldText: (fieldId, key, lang, text) =>
      edit((s) =>
        setOrPrune(s.overlay, ['fields', fieldId, key, lang], text, readPath(s.base?.fields?.[fieldId], [key, lang])),
      ),
    setLanguages: (languages) =>
      edit((s) => setOrPrune(s.overlay, ['settings', 'languages'], languages, s.base?.languages)),
    setValidation: (v) => set({ validation: v }),
    isDirty: () => !sameContent(get().overlay, get().savedDraft ?? {}),
    overlaySnapshot: () => clone(get().overlay),
  }
})
